import clsx from 'clsx'
import React, { FC, useEffect } from "react"
import { ListboxButton, ListboxOption } from "@headlessui/react"

import { PhaseContext } from "../../../utils/Interfaces"
import { generateUniqueId } from "../../../utils/Helpers"
import { ListBox } from '../../../lib/fragments/ListBox'
import { InputBasic } from '../../../components/utils/InputBasic'
import { ToggleSwitch } from '../../../components/utils/ToggleSwitch'
import { InputTextArea } from '../../../components/utils/InputTextArea'
import { BorderlessFloatingInput } from '../../../components/utils/BorderlessFloatingInput'
import { DataSchemaTypes, PRESENTATION_MODES, TEXT_TRANSFORMATION } from '../../../utils/Constants'
import { generateDefaultValue, reconcilePhaseState, resolveManifestLogic } from '../../../lib/phases/PhaseHelper'

export const ProfileDefinitionPhaser: FC<PhaseContext> = ({ phase, manifest, state, setstate, values, faults, definitions, listboxHandler, blurHandler, changeHandler, toggleHandler }) => {
    const currentPhaseSeq = phase.sequence + 1

    /**
     * Reconciles the persistent state value with the current allowed schema options.
     * If the existing value is non-compliant, it enforces the system's index-0 default.
     * 
     */
    useEffect(() => {
        // Guard: Don't reconcile while posting
        if (state.posting) return;

        // Optimization: Use a localized reference of values from state
        // This ensures we aren't tracking 'values' itself as a dependency
        const currentValues = values;
        if (!currentValues?.type) return;

        let hasChanges = false;
        const updatedValues = { ...currentValues };

        definitions.forEach((definition: any) => {
            // Skip layout helpers
            if (['sub-header', 'divider'].includes(definition.type)) return;

            const showDefinition = definition.visibility ? resolveManifestLogic(definition?.visibility, currentValues) : true;
            const currentValue = currentValues[definition.name];

            if (!showDefinition) {
                // Logic: If hidden, reset to system default (e.g., '', null, or false)
                const defaultValue = generateDefaultValue(definition);

                // Direct Comparison (Primitive or Object Key)
                const isDifferent = typeof defaultValue === 'object'
                    ? currentValue?.key !== defaultValue
                    : currentValue !== defaultValue;

                if (isDifferent) {
                    updatedValues[definition.name] = defaultValue;
                    hasChanges = true;
                }
            } else if (definition?.display_as === 'listbox') {
                // Logic: Ensure selected listbox option exists in the current filtered list
                const options = getBaseOptions(definition);
                const isCompliant = options.some((opt: any) => opt.key === currentValue?.key);

                if (!isCompliant && options.length > 0) {
                    updatedValues[definition.name] = options[0]; // Force Index 0
                    hasChanges = true;
                }
            }
        });

        if (hasChanges) {
            setstate((prev: any) => {
                // Double-check: are the values actually different from the current state?
                // This is the "Final Firewall" against the infinite loop
                const nextInput = reconcilePhaseState(phase.slug, updatedValues, prev.input);

                // If the reconcile result is identical to previous, return prev to stop re-render
                if (JSON.stringify(prev.input) === JSON.stringify(nextInput)) {
                    return prev;
                }

                return {
                    ...prev,
                    input: nextInput
                };
            });
        }

        /**
         *  REMOVED 'values' from dependencies.
         * We only want to re-run this when the definitions change 
         * or when a 'Master' trigger (like a specific key change) occurs.
         */
    }, [state, definitions]);

    const getBaseOptions = (definition: any) => {
        // If Options is an object type return the object
        if (typeof definition.options === 'object') return definition.options

        switch (definition.options) {
            case 'DATA_SCHEMA_TYPES':
                return DataSchemaTypes.map(type => ({
                    key: type.key,
                    label: type.label,
                    icon: type.icon,
                    desc: type.desc
                }))

            case 'TEXT_TRANSFORMATION':
                return TEXT_TRANSFORMATION
                    .filter(type => values.capacity === 'standard' || type.key === 'mixed')
                    .map(type => ({ key: type.key, label: type.label }));

            case 'DYNAMIC_VALIDATIONS':
                const { rules = [] } = definition.visibility || {};
                const schemaRule = rules.find((r: any) => r.operator === 'has_schema_options');

                if (!schemaRule) return [];

                //  Type-Specific Switch Case Logic
                const dependentValue = values[schemaRule.depends_on];
                const activeKey = (dependentValue && typeof dependentValue === 'object')
                    ? (Array.isArray(dependentValue) ? dependentValue[0]?.key : dependentValue?.key)
                    : dependentValue;

                if (!activeKey) return [];

                const selectedDataSchema = DataSchemaTypes.find(t => t.key === activeKey);
                const rawValidations = selectedDataSchema?.validations ?? [];

                // Optimized Filter
                return rawValidations.filter((val: any) => {
                    const key = val?.key;

                    switch (values.type?.key) {
                        case 'numeric':
                            // Rule A: block 'amounts', but still allow other validation
                            if (key === 'amounts') return false
                            break

                        case 'text':
                            // Rule B: Capacity Logic (Standard vs Extended)
                            const isExtended = values.capacity === 'extended';
                            const isComments = key === 'comments_descriptions';
                            return isExtended ? isComments : !isComments;

                        default:
                            // Fallback for types not explicitly defined
                            break;
                    }

                    // Global Fallback Rule (prevents returning undefined)
                    return true;
                }).map((val: any) => ({
                    key: val.key,
                    label: val.label,
                }));

            case 'PRESENTATION_MODES':
                return PRESENTATION_MODES.map(type => ({
                    key: type.key,
                    label: type.label,
                }))

            default:
                return []
        }
    }

    return (
        <React.Fragment>
            <div className="flex md:flex-row flex-col-reverse gap-3">
                <div className="w-full text-xl font-medium text-slate-700 dark:text-slate-300 uppercase">
                    {phase.title}
                </div>

                <div className="w-32 md:float-start float-start">
                    <span className="text-sm text-orange-500 md:text-end block">
                        Step: {currentPhaseSeq} of {manifest.length}
                    </span>
                </div>
            </div>

            <div className="w-full pb-4">
                <span className="block text-xs text-slate-500 dark:text-slate-400 py-1 normal-case font-normal">
                    {phase.caption}
                </span>
            </div>

            <div className="w-full space-y-3 pb-20">
                {
                    definitions.map((definition: any, index: number) => {
                        // Visibility Check
                        const showDefinition = definition?.visibility
                            ? resolveManifestLogic(definition?.visibility, values)
                            : true;

                        // If the field shouldn't be rendered based on current state, exit immediately.
                        if (!showDefinition) return

                        let options: any = null
                        const currentLayoutGroup = definition?.layout;
                        const prevDefinition = definitions[index - 1];
                        const nextDefinition: any = definitions[index + 1];                        

                        // If PREVIOUS item was the part of a layout group, exit immediately.
                        if (definition?.layout && prevDefinition?.layout === definition?.layout) return

                        // Base mandatory requirement
                        let isMandatory = definition?.mand ?? false

                        // Logic Bridge: If not mandatory, check the 'Mandates' ruleset
                        if (!isMandatory && definition?.mandates) {
                            /** 
                             * Reuse the same logic engine 'resolveManifestLogic'.
                             * If the rules pass (e.g., boundaries are 'enforced'), 'isMandatory' flips to true.
                             */                            
                            isMandatory = resolveManifestLogic(definition?.mandates, values);
                        }

                        switch (definition.type) {
                            case 'sub-header':
                                return showDefinition && (
                                    <div key={generateUniqueId(definition.title)} className={clsx(
                                        index !== 0 && "pt-4",
                                        "w-full pb-1 mb-2"
                                    )}>
                                        <h4 className="text-sm font-bold uppercase tracking-widest text-orange-400 dark:text-orange-500">
                                            {definition.title}
                                        </h4>

                                        {definition.caption && (
                                            <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400 font-normal">
                                                {definition.caption}
                                            </p>
                                        )}
                                    </div>
                                );

                            case 'text':
                            case 'numeric':
                                if (currentLayoutGroup) {
                                    return (
                                        <div key={generateUniqueId(currentLayoutGroup)} className="w-full flex md:flex-row flex-col align-middle gap-2">
                                            <div className="flex-1">
                                                <BorderlessFloatingInput
                                                    required={isMandatory}
                                                    name={definition.name}
                                                    label={definition.title}
                                                    placeHolder={definition?.caption}
                                                    value={values[definition.name]}
                                                    error={faults[definition.name]}
                                                    posting={state.posting}
                                                    changeHandler={changeHandler}
                                                    blurHandler={(e: any) => blurHandler(e, phase)}
                                                />
                                            </div>

                                            <div className="flex-1">
                                                <BorderlessFloatingInput
                                                    required={isMandatory}
                                                    posting={state.posting}
                                                    name={nextDefinition.name}
                                                    label={nextDefinition.title}
                                                    changeHandler={changeHandler}
                                                    value={values[nextDefinition.name]}
                                                    error={faults[nextDefinition.name]}
                                                    placeHolder={nextDefinition?.caption}
                                                    blurHandler={(e: any) => blurHandler(e, phase)}
                                                />
                                            </div>
                                        </div>
                                    );
                                }

                                return <div key={generateUniqueId(definition.title)} className="w-full pb-2">
                                    {
                                        definition.multiline ? (
                                            <InputTextArea
                                                required={isMandatory}
                                                posting={state.posting}
                                                label={definition.title}
                                                name={definition.name}
                                                changeHandler={changeHandler}
                                                value={values[definition.name]}
                                                error={faults[definition.name]}
                                                placeHolder={definition.caption}
                                                blurHandler={(e: any) => blurHandler(e, phase)}
                                            />
                                        ) : (
                                            <InputBasic
                                                required={isMandatory}
                                                name={definition.name}
                                                posting={state.posting}
                                                label={definition.title}
                                                changeHandler={changeHandler}
                                                value={values[definition.name]}
                                                error={faults[definition.name]}
                                                placeHolder={definition?.caption}
                                                blurHandler={(e: any) => blurHandler(e, phase)}
                                            />
                                        )
                                    }
                                </div>

                            case 'toggle':
                                const isChecked = values[definition.name] === definition.toggles?.true?.key;

                                // Check if the next item is a toggle, AND is currently visible.
                                const isNextAlsoVisibleToggle =
                                    nextDefinition &&
                                    nextDefinition.type === 'toggle' &&
                                    showDefinition;

                                return showDefinition && <div key={generateUniqueId(definition.title)} className={clsx(
                                    "w-full pb-3",
                                    isNextAlsoVisibleToggle && "border-b border-slate-300 dark:border-slate-800"
                                )}
                                >
                                    <ToggleSwitch
                                        checked={isChecked}
                                        name={definition.name}
                                        label={definition.title}
                                        description={definition.caption}
                                        changeHandler={(e: any) => toggleHandler(e, phase)}
                                    />
                                </div>

                            case 'predefined':
                                options = getBaseOptions(definition)

                                switch (definition.display_as) {
                                    case 'radio': return
                                    case 'checkbox': return
                                    default:
                                        return showDefinition && <div key={generateUniqueId(definition.title)} className="pb-2">
                                            <ListBox
                                                value={values[definition.name]}
                                                eventHandler={(e: any) => listboxHandler(e, definition.name, phase)}
                                                label={
                                                    <>
                                                        <p className="text-sm font- text-slate-600 dark:text-slate-300 tracking-wide font-medium">
                                                            {definition.title}:
                                                        </p>

                                                        <p className="w-full text-slate-500 dark:text-slate-400 pb-1 text-sm">
                                                            {definition.caption}
                                                        </p>
                                                    </>
                                                }
                                                button={
                                                    <ListboxButton className="relative w-full bg-white border border-slate-300 rounded-md py-2.5 pl-1 pr-3 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 sm:text-sm">
                                                        <span className="flex w-full items-center align-middle">
                                                            <div className="flex-grow flex items-center">
                                                                <span className="truncate ml-3 font-normal group-data-[selected]:font-semibold">
                                                                    {
                                                                        values[definition.name] ? (
                                                                            <div className="flex items-center gap-x-4">
                                                                                <span className="truncate group-data-[selected]:font-semibold">
                                                                                    {values[definition.name]?.label}
                                                                                </span>
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-slate-500 dark:text-slate-300">
                                                                                Select {definition.title}
                                                                            </span>
                                                                        )
                                                                    }
                                                                </span>
                                                            </div>

                                                            <span className="fa-light fa-chevron-down float-end"></span>
                                                        </span>

                                                    </ListboxButton>
                                                }
                                                options={
                                                    options.map((option: any) => (
                                                        <ListboxOption key={`$Y-opt-${option.key}`} value={option} className="group relative cursor-default select-none py-2 pl-3 pr-9 data-[selected]:text-orange-600 data-[focus]:bg-orange-100 dark:data-[focus]:bg-orange-900/70 data-[focus]:text-orange-700 dark:data-[focus]:text-orange-500 rounded-md w-full">
                                                            <div className="flex items-center gap-x-4">
                                                                <span className="truncate group-data-[selected]:font-medium">
                                                                    {option.label}

                                                                    <span className="block text-xs text-slate-500 dark:text-slate-400">
                                                                        {option.desc}
                                                                    </span>
                                                                </span>
                                                            </div>

                                                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-orange-600 group-[&:not([data-selected])]:hidden">
                                                                <span className="fa-light fa-check"></span>
                                                            </span>
                                                        </ListboxOption>
                                                    ))
                                                }
                                            />
                                        </div>

                                }
                        }
                    })
                }
            </div>
        </React.Fragment>
    )
}